import type { FilterOptions } from "@gw/shared-types";
import { DIVISION_HINT, MANAGER_HINT } from "@/features/shared/ownerGroupLabels";
import type { OperationsState } from "../operationsSchema";
import { workTypeLabel } from "../operationsLabels";
import { MultiSelect, type MultiSelectOption } from "./MultiSelect";

interface Props {
  state: OperationsState;
  options: FilterOptions | undefined;
  onChange: (next: Partial<OperationsState>) => void;
}

/**
 * The API lists only values carried by at least one open request, alphabetically, and the counts it
 * sends are deliberately not shown: the lists do not cascade, so a number next to a value would be
 * wrong as soon as two filters are combined.
 */
const TEAM_HINT = "The owner's team: the manager in the provisioning directory, matched by the e-mail. A journey follows its request's owner, or its intake's while only the intake exists. No team = owners the directory does not know.";
const LIST_HINT = "Only values with open work right now";
const REQUEST_TYPE_HINT = "Only values with open work right now. Onboarding = the whole journey, intake included.";

const BUCKET_ONBOARDING = "bucket:onboarding";
const BUCKET_REDEPLOYMENT = "bucket:redeployment";

/** The multi-select's value list: the two buckets (when on) followed by the raw types. */
export function requestTypeSelection(state: Pick<OperationsState, "onboardingsOnly" | "redeployment" | "requestType">): string[] {
  return [
    ...(state.onboardingsOnly ? [BUCKET_ONBOARDING] : []),
    ...(state.redeployment ? [BUCKET_REDEPLOYMENT] : []),
    ...state.requestType,
  ];
}

/** Inverse of requestTypeSelection: buckets become flags, everything else is a raw type. */
export function requestTypeStateFrom(values: string[]): Pick<OperationsState, "onboardingsOnly" | "redeployment" | "requestType"> {
  return {
    onboardingsOnly: values.includes(BUCKET_ONBOARDING),
    redeployment: values.includes(BUCKET_REDEPLOYMENT),
    requestType: values.filter((v) => v !== BUCKET_ONBOARDING && v !== BUCKET_REDEPLOYMENT),
  };
}

/** Options whose value is also the label (owners, clients, LOBs, engagement types, countries). */
function plainOptions(values: { value: string }[] | undefined): MultiSelectOption[] {
  return (values ?? []).map((o) => ({ value: o.value, label: o.value }));
}

/**
 * Request-type options: the two derived buckets plus every raw type except the ones the buckets
 * already cover (Onboarding, Redeployment, and the pre-verification types: on this screen "Onboarding"
 * is the journey), in one alphabetical list by display label (the buckets are not pinned to the top).
 */
export function requestTypeOptionsFrom(rawTypes: { value: string }[]): MultiSelectOption[] {
  return [
    { value: BUCKET_ONBOARDING, label: "Onboarding" },
    { value: BUCKET_REDEPLOYMENT, label: "Redeployment" },
    ...rawTypes
      .filter((o) => o.value !== "Onboarding" && o.value !== "Redeployment" && !o.value.startsWith("PreOnboard"))
      .map((o) => ({ value: o.value, label: workTypeLabel(o.value) })),
  ].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * Panel 1: one row of multi-selects over the summary's own filter options (values OR within a
 * control, AND across controls), plus the period toggle. Filters only: the view controls (attention
 * lens) live in the page head, and "Clear filters" on the breadcrumb line.
 * Deliberately a bar, not a rail: the deck is one screen.
 */
/** Any slicer or lens away from its default: what "Clear filters" would undo. */
export function hasActiveFilters(state: OperationsState): boolean {
  return (
    state.onboardingsOnly || state.redeployment || !!state.lens || !!state.pen ||
    [state.requestType, state.client, state.lob, state.eng, state.assigned, state.team, state.division, state.manager, state.country].some((a) => a.length > 0)
  );
}

export function SlicerBar({ state, options, onChange }: Props) {

  const requestTypeOptions = requestTypeOptionsFrom(options?.requestTypes ?? []);

  return (
    <section className="ops-slicer" aria-label="Filters">
      <div className="ops-ctl ops-ctl-period">
        <label>Period</label>
        <div className="unit-toggle" role="tablist" aria-label="Period">
          <button role="tab" aria-selected={state.period === "today"} className={state.period === "today" ? "on" : ""} onClick={() => onChange({ period: "today" })}>
            Today
          </button>
          <button role="tab" aria-selected={state.period === "wtd"} className={state.period === "wtd" ? "on" : ""} onClick={() => onChange({ period: "wtd" })}>
            Week to date
          </button>
        </div>
      </div>

      <MultiSelect id="ops-division" label="Division" hint={DIVISION_HINT} options={plainOptions(options?.divisions)} selected={state.division} onChange={(division) => onChange({ division })} />
      <MultiSelect id="ops-manager" label="Manager" hint={MANAGER_HINT} options={plainOptions(options?.managers)} selected={state.manager} onChange={(manager) => onChange({ manager })} />
      <MultiSelect id="ops-team" label="Team" hint={TEAM_HINT} options={plainOptions(options?.teams)} selected={state.team} onChange={(team) => onChange({ team })} />
      <MultiSelect id="ops-owner" label="Owner" hint={LIST_HINT} options={plainOptions(options?.assignedUsers)} selected={state.assigned} onChange={(assigned) => onChange({ assigned })} />
      <MultiSelect id="ops-client" label="Client" hint={LIST_HINT} options={plainOptions(options?.clients)} selected={state.client} onChange={(client) => onChange({ client })} />
      <MultiSelect id="ops-lob" label="Line of business" hint={LIST_HINT} options={plainOptions(options?.linesOfBusiness)} selected={state.lob} onChange={(lob) => onChange({ lob })} />
      <MultiSelect id="ops-eng" label="Engagement type" hint={LIST_HINT} options={plainOptions(options?.engagementTypes)} selected={state.eng} onChange={(eng) => onChange({ eng })} />

      <MultiSelect
        id="ops-type"
        label="Request type"
        hint={REQUEST_TYPE_HINT}
        options={requestTypeOptions}
        selected={requestTypeSelection(state)}
        onChange={(values) => onChange(requestTypeStateFrom(values))}
      />

      <MultiSelect id="ops-country" label="Country" hint={LIST_HINT} options={plainOptions(options?.workerCountries)} selected={state.country} onChange={(country) => onChange({ country })} />

    </section>
  );
}
